import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTheme } from '../../contexts/ThemeContext';
import { useSidebar } from '../../contexts/SidebarContext';
import { 
  Wallet, 
  TrendingUp, 
  TrendingDown, 
  CreditCard,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Filter
} from 'lucide-react';
import { 
  PieChart, 
  Pie, 
  Cell, 
  LineChart, 
  Line, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import { toast } from 'react-hot-toast';

const API = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8001';

const DashboardFinanceiro = () => {
  const { theme: themeColors, isDarkMode } = useTheme();
  const { isMobile } = useSidebar();
  
  // States
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [mesAno, setMesAno] = useState(() => {
    const hoje = new Date();
    return { mes: hoje.getMonth() + 1, ano: hoje.getFullYear() };
  });
  
  // Estados para o filtro customizado
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [tempMes, setTempMes] = useState(mesAno.mes);
  const [tempAno, setTempAno] = useState(mesAno.ano);
  
  // Estado para filtro de categoria
  const [categoriaSelecionada, setCategoriaSelecionada] = useState('todas');
  const [despesasPorCategoria, setDespesasPorCategoria] = useState([]);
  const [categorias, setCategorias] = useState([]);

  // Carregar dados do dashboard
  const loadDashboard = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/financeiro/dashboard`, {
        params: { mes: mesAno.mes, ano: mesAno.ano },
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(response.data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error('Erro ao carregar dashboard financeiro');
    } finally {
      setLoading(false);
    }
  };

  // Carregar categorias
  const loadCategorias = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/financeiro/categorias`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategorias(response.data.categorias || []);
    } catch (error) {
      console.error('Error loading categorias:', error);
    }
  };

  useEffect(() => {
    loadDashboard();
    loadCategorias();
  }, [mesAno]);

  // Carregar despesas por categoria
  const loadDespesasPorCategoria = async (categoriaId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/financeiro/despesas-por-categoria`, {
        params: { 
          categoria_id: categoriaId === 'todas' ? null : categoriaId,
          mes: mesAno.mes, 
          ano: mesAno.ano 
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      setDespesasPorCategoria(response.data.despesas || []);
    } catch (error) {
      console.error('Error loading despesas por categoria:', error);
      toast.error('Erro ao carregar despesas da categoria');
    }
  };

  // Carregar quando categoria muda
  useEffect(() => {
    if (categoriaSelecionada) {
      loadDespesasPorCategoria(categoriaSelecionada);
    }
  }, [categoriaSelecionada, mesAno]);
  
  // Fechar date picker quando clicar fora
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showDatePicker && !e.target.closest('[data-date-picker]')) {
        setShowDatePicker(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDatePicker]);

  // Funções auxiliares
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result 
      ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
      : '139, 92, 246'; // fallback roxo
  };

  const handlePreviousMonth = () => {
    setMesAno(prev => {
      let novoMes = prev.mes - 1;
      let novoAno = prev.ano;
      if (novoMes < 1) {
        novoMes = 12;
        novoAno -= 1;
      }
      return { mes: novoMes, ano: novoAno };
    });
  };

  const handleNextMonth = () => {
    setMesAno(prev => {
      let novoMes = prev.mes + 1;
      let novoAno = prev.ano;
      if (novoMes > 12) {
        novoMes = 1;
        novoAno += 1;
      }
      return { mes: novoMes, ano: novoAno };
    });
  };

  const getMesNome = (mes) => {
    const meses = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    return meses[mes - 1];
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px',
        color: themeColors.text 
      }}>
        Carregando dashboard...
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div style={{ color: themeColors.text }}>
        Erro ao carregar dashboard
      </div>
    );
  }

  const { cards_principais, graficos, frequencia_gastos, pendencias } = dashboard;

  // Cores para gráficos
  const CHART_COLORS = [
    '#06B6D4', '#8B5CF6', '#F59E0B', '#10B981', '#EF4444', '#EC4899'
  ];

  return (
    <div>
      {/* Header com Navegação de Mês e Ano - FILTRO CUSTOMIZADO ELEGANTE */}
      <div style={{ marginBottom: '32px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '20px' }}>
          <h2 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: themeColors.text
          }}>
            Dashboard Financeiro
          </h2>

          {/* Filtro de Data Customizado - Estilo Transações */}
          <div style={{ position: 'relative' }} data-date-picker>
            <button
              onClick={() => {
                setTempMes(mesAno.mes);
                setTempAno(mesAno.ano);
                setShowDatePicker(!showDatePicker);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '10px 20px',
                borderRadius: '12px',
                background: isDarkMode 
                  ? 'linear-gradient(135deg, rgba(6, 182, 212, 0.15), rgba(59, 130, 246, 0.15))'
                  : 'linear-gradient(135deg, rgba(6, 182, 212, 0.1), rgba(59, 130, 246, 0.1))',
                border: `1px solid ${isDarkMode ? 'rgba(6, 182, 212, 0.3)' : 'rgba(6, 182, 212, 0.2)'}`,
                boxShadow: isDarkMode 
                  ? '0 4px 12px rgba(6, 182, 212, 0.15)' 
                  : '0 2px 8px rgba(6, 182, 212, 0.1)',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                color: themeColors.text,
                fontSize: '14px',
                fontWeight: '600',
                minWidth: '180px',
                justifyContent: 'center'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = themeColors.accent;
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = isDarkMode ? 'rgba(6, 182, 212, 0.3)' : 'rgba(6, 182, 212, 0.2)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <span style={{ textTransform: 'capitalize' }}>
                {['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 
                  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'][mesAno.mes - 1]} {mesAno.ano}
              </span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

            {/* Date Picker Modal - Estilo Mobills */}
            {showDatePicker && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: '8px',
                  background: isDarkMode ? '#252b3b' : 'white',
                  border: `1px solid ${themeColors.border}`,
                  borderRadius: '12px',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                  zIndex: 1000,
                  width: '280px',
                  overflow: 'hidden',
                  padding: '16px'
                }}
              >
                {/* Year Selector */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '16px',
                  marginBottom: '16px',
                  padding: '12px',
                  background: 'linear-gradient(135deg, #06B6D4, #3B82F6)',
                  borderRadius: '10px'
                }}>
                  <button
                    onClick={() => setTempAno(tempAno - 1)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: 'white',
                      cursor: 'pointer',
                      padding: '2px',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  <span style={{
                    color: 'white',
                    fontSize: '18px',
                    fontWeight: '700',
                    minWidth: '70px',
                    textAlign: 'center'
                  }}>
                    {tempAno}
                  </span>
                  <button
                    onClick={() => setTempAno(tempAno + 1)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: 'white',
                      cursor: 'pointer',
                      padding: '2px',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>

                {/* Month Grid */}
                <div className="cq-grid-4" style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
                  gap: '6px',
                  marginBottom: '16px'
                }}>
                  {['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'].map((mes, index) => (
                    <button
                      key={mes}
                      onClick={() => {
                        setTempMes(index + 1);
                        setMesAno({ mes: index + 1, ano: tempAno });
                        setShowDatePicker(false);
                      }}
                      style={{
                        padding: '8px 4px',
                        borderRadius: '6px',
                        border: 'none',
                        background: (mesAno.mes === index + 1 && mesAno.ano === tempAno)
                          ? 'linear-gradient(135deg, #06B6D4, #3B82F6)' 
                          : (isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)'),
                        color: (mesAno.mes === index + 1 && mesAno.ano === tempAno) ? 'white' : themeColors.text,
                        fontSize: '11px',
                        fontWeight: (mesAno.mes === index + 1 && mesAno.ano === tempAno) ? '700' : '500',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (!(mesAno.mes === index + 1 && mesAno.ano === tempAno)) {
                          e.currentTarget.style.background = isDarkMode 
                            ? 'rgba(6, 182, 212, 0.2)' 
                            : 'rgba(6, 182, 212, 0.1)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!(mesAno.mes === index + 1 && mesAno.ano === tempAno)) {
                          e.currentTarget.style.background = isDarkMode 
                            ? 'rgba(255, 255, 255, 0.05)' 
                            : 'rgba(0, 0, 0, 0.03)';
                        }
                      }}
                    >
                      {mes}
                    </button>
                  ))}
                </div>

                {/* Action Buttons */}
                <div style={{
                  display: 'flex',
                  gap: '8px',
                  justifyContent: 'space-between'
                }}>
                  <button
                    onClick={() => setShowDatePicker(false)}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      borderRadius: '8px',
                      border: `1px solid ${themeColors.border}`,
                      background: 'transparent',
                      color: themeColors.text,
                      fontSize: '11px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px'
                    }}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={() => {
                      const now = new Date();
                      setTempMes(now.getMonth() + 1);
                      setTempAno(now.getFullYear());
                      setMesAno({ mes: now.getMonth() + 1, ano: now.getFullYear() });
                      setShowDatePicker(false);
                    }}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      borderRadius: '8px',
                      border: 'none',
                      background: 'linear-gradient(135deg, #06B6D4, #3B82F6)',
                      color: 'white',
                      fontSize: '11px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      boxShadow: '0 2px 8px rgba(6, 182, 212, 0.3)'
                    }}
                  >
                    Atual
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Cards Principais */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: '16px',
        marginBottom: '24px'
      }}>
        {/* Saldo Atual */}
        <div style={{
          background: isDarkMode ? '#252b3b' : 'white',
          borderRadius: '16px',
          padding: '24px',
          border: `1px solid ${themeColors.border}`,
          height: 'fit-content'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <p style={{ fontSize: '14px', color: themeColors.textSecondary, marginBottom: '8px' }}>
                Saldo Acumulado
              </p>
              <p style={{
                fontSize: '24px',
                fontWeight: '700',
                color: cards_principais?.saldo_acumulado >= 0 ? '#10B981' : '#EF4444'
              }}>
                {formatCurrency(cards_principais?.saldo_acumulado || 0)}
              </p>
            </div>
            <div style={{
              padding: '12px',
              borderRadius: '12px',
              background: 'rgba(6, 182, 212, 0.1)'
            }}>
              <Wallet size={24} color="#06B6D4" />
            </div>
          </div>
        </div>

        {/* Receitas */}
        <div style={{
          background: isDarkMode ? '#252b3b' : 'white',
          borderRadius: '16px',
          padding: '24px',
          border: `1px solid ${themeColors.border}`,
          height: 'fit-content'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <p style={{ fontSize: '14px', color: themeColors.textSecondary, marginBottom: '8px' }}>
                Receitas
              </p>
              <p style={{
                fontSize: '24px',
                fontWeight: '700',
                color: '#10B981'
              }}>
                {formatCurrency(cards_principais.receitas)}
              </p>
            </div>
            <div style={{
              padding: '12px',
              borderRadius: '12px',
              background: 'rgba(16, 185, 129, 0.1)'
            }}>
              <TrendingUp size={24} color="#10B981" />
            </div>
          </div>
        </div>

        {/* Despesas */}
        <div style={{
          background: isDarkMode ? '#252b3b' : 'white',
          borderRadius: '16px',
          padding: '24px',
          border: `1px solid ${themeColors.border}`,
          height: 'fit-content'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <p style={{ fontSize: '14px', color: themeColors.textSecondary, marginBottom: '8px' }}>
                Despesas
              </p>
              <p style={{
                fontSize: '24px',
                fontWeight: '700',
                color: '#EF4444'
              }}>
                {formatCurrency(cards_principais.despesas)}
              </p>
            </div>
            <div style={{
              padding: '12px',
              borderRadius: '12px',
              background: 'rgba(239, 68, 68, 0.1)'
            }}>
              <TrendingDown size={24} color="#EF4444" />
            </div>
          </div>
        </div>

        {/* Cartões de Crédito */}
        <div style={{
          background: isDarkMode ? '#252b3b' : 'white',
          borderRadius: '16px',
          padding: '24px',
          border: `1px solid ${themeColors.border}`,
          height: 'fit-content'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <p style={{ fontSize: '14px', color: themeColors.textSecondary, marginBottom: '8px' }}>
                Faturas em Aberto
              </p>
              <p style={{
                fontSize: '24px',
                fontWeight: '700',
                color: '#8B5CF6'
              }}>
                {formatCurrency(cards_principais.cartoes_credito)}
              </p>
            </div>
            <div style={{
              padding: '12px',
              borderRadius: '12px',
              background: 'rgba(139, 92, 246, 0.1)'
            }}>
              <CreditCard size={24} color="#8B5CF6" />
            </div>
          </div>
        </div>
      </div>

      {/* Gráficos - Linha 1 */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(400px, 1fr))',
        gap: '20px',
        marginBottom: '32px'
      }}>
        {/* Despesas por Categoria - Donut Chart */}
        <div style={{
          background: isDarkMode ? '#252b3b' : 'white',
          borderRadius: '16px',
          padding: '24px',
          border: `1px solid ${themeColors.border}`
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: themeColors.text,
            marginBottom: '20px'
          }}>
            Despesas por Categoria
          </h3>
          
          {graficos.despesas_por_categoria.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={graficos.despesas_por_categoria}
                  dataKey="valor"
                  nameKey="categoria"
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                >
                  {graficos.despesas_por_categoria.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.cor || CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value) => formatCurrency(value)}
                  contentStyle={{
                    background: isDarkMode ? '#1f2937' : 'white',
                    border: `1px solid ${themeColors.border}`,
                    borderRadius: '8px'
                  }}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div style={{
              height: '300px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: themeColors.textSecondary
            }}>
              Nenhuma despesa neste mês
            </div>
          )}
        </div>

        {/* Balanço Mensal - Line Chart */}
        <div style={{
          background: isDarkMode ? '#252b3b' : 'white',
          borderRadius: '16px',
          padding: '24px',
          border: `1px solid ${themeColors.border}`
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: themeColors.text,
            marginBottom: '20px'
          }}>
            Balanço Mensal (Últimos 6 Meses)
          </h3>
          
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={graficos.balanco_mensal}>
              <CartesianGrid strokeDasharray="3 3" stroke={themeColors.border} />
              <XAxis dataKey="mes" stroke={themeColors.textSecondary} />
              <YAxis stroke={themeColors.textSecondary} />
              <Tooltip 
                formatter={(value) => formatCurrency(value)}
                contentStyle={{
                  background: isDarkMode ? '#1f2937' : 'white',
                  border: `1px solid ${themeColors.border}`,
                  borderRadius: '8px'
                }}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="balanco" 
                stroke="#8B5CF6" 
                strokeWidth={2}
                name="Balanço"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Gráficos - Linha 2 */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(400px, 1fr))',
        gap: '20px',
        marginBottom: '32px'
      }}>
        {/* Receitas x Despesas - Bar Chart */}
        <div style={{
          background: isDarkMode ? '#252b3b' : 'white',
          borderRadius: '16px',
          padding: '24px',
          border: `1px solid ${themeColors.border}`
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: themeColors.text,
            marginBottom: '20px'
          }}>
            Receitas x Despesas
          </h3>
          
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={graficos.receitas_despesas_comparacao}>
              <CartesianGrid strokeDasharray="3 3" stroke={themeColors.border} />
              <XAxis dataKey="mes" stroke={themeColors.textSecondary} />
              <YAxis stroke={themeColors.textSecondary} />
              <Tooltip 
                formatter={(value) => formatCurrency(value)}
                contentStyle={{
                  background: isDarkMode ? '#1f2937' : 'white',
                  border: `1px solid ${themeColors.border}`,
                  borderRadius: '8px'
                }}
              />
              <Legend />
              <Bar dataKey="receitas" fill="#10B981" name="Receitas" />
              <Bar dataKey="despesas" fill="#EF4444" name="Despesas" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Frequência de Gastos */}
        <div style={{
          background: isDarkMode ? '#252b3b' : 'white',
          borderRadius: '16px',
          padding: '24px',
          border: `1px solid ${themeColors.border}`
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: themeColors.text,
            marginBottom: '20px'
          }}>
            Últimas Despesas
          </h3>
          
          {frequencia_gastos.length > 0 ? (
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {frequencia_gastos.map((gasto, index) => (
                <div
                  key={index}
                  style={{
                    padding: '12px',
                    borderRadius: '8px',
                    background: isDarkMode ? '#1f2937' : '#f9fafb',
                    marginBottom: '8px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <p style={{
                      fontSize: '14px',
                      fontWeight: '500',
                      color: themeColors.text,
                      marginBottom: '4px'
                    }}>
                      {gasto.descricao}
                    </p>
                    <p style={{
                      fontSize: '12px',
                      color: themeColors.textSecondary
                    }}>
                      {gasto.categoria || 'Sem categoria'} • {new Date(gasto.data).toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                  <p style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#EF4444'
                  }}>
                    {formatCurrency(gasto.valor)}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <div style={{
              height: '300px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: themeColors.textSecondary
            }}>
              Nenhuma despesa registrada neste mês
            </div>
          )}
        </div>

        {/* Despesas por Categoria */}
        <div style={{
          background: isDarkMode ? '#252b3b' : 'white',
          borderRadius: '16px',
          padding: '24px',
          border: `1px solid ${themeColors.border}`
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '20px'
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: themeColors.text
            }}>
              Despesas por Categoria
            </h3>
            
            {/* Dropdown de Categorias - Estilo Elegante */}
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => {
                  const dropdown = document.getElementById('categoria-dropdown');
                  dropdown.style.display = dropdown.style.display === 'none' ? 'block' : 'none';
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 16px',
                  borderRadius: '10px',
                  border: `2px solid ${themeColors.border}`,
                  background: isDarkMode ? '#1a1f2e' : 'white',
                  color: themeColors.text,
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  outline: 'none',
                  transition: 'all 0.2s ease',
                  minWidth: '200px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = themeColors.accent;
                  e.currentTarget.style.boxShadow = `0 0 0 3px ${isDarkMode ? 'rgba(6, 182, 212, 0.1)' : 'rgba(6, 182, 212, 0.05)'}`;
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = themeColors.border;
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                {/* Bolinha colorida */}
                <div style={{
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  background: categoriaSelecionada === 'todas' ? '#8B5CF6' : 
                             (categorias.find(c => c.id === categoriaSelecionada)?.cor || '#8B5CF6')
                }} />
                {categoriaSelecionada === 'todas' 
                  ? 'Todas as Categorias' 
                  : (categorias.find(c => c.id === categoriaSelecionada)?.nome || 'Selecione...')}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ marginLeft: 'auto' }}>
                  <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>

              {/* Dropdown Menu */}
              <div
                id="categoria-dropdown"
                style={{
                  display: 'none',
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: '8px',
                  background: isDarkMode ? '#252b3b' : 'white',
                  border: `1px solid ${themeColors.border}`,
                  borderRadius: '12px',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                  zIndex: 1000,
                  minWidth: '240px',
                  maxHeight: '320px',
                  overflowY: 'auto'
                }}
              >
                <button
                  onClick={() => {
                    setCategoriaSelecionada('todas');
                    document.getElementById('categoria-dropdown').style.display = 'none';
                  }}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    background: categoriaSelecionada === 'todas' 
                      ? (isDarkMode ? 'rgba(139, 92, 246, 0.1)' : 'rgba(139, 92, 246, 0.05)')
                      : 'transparent',
                    border: 'none',
                    color: themeColors.text,
                    fontSize: '14px',
                    fontWeight: '500',
                    textAlign: 'left',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (categoriaSelecionada !== 'todas') {
                      e.currentTarget.style.background = isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (categoriaSelecionada !== 'todas') {
                      e.currentTarget.style.background = 'transparent';
                    }
                  }}
                >
                  <div style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    background: '#8B5CF6'
                  }} />
                  Todas as Categorias
                </button>
                
                {categorias.filter(cat => cat.tipo === 'despesa').map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => {
                      setCategoriaSelecionada(cat.id);
                      document.getElementById('categoria-dropdown').style.display = 'none';
                    }}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      background: categoriaSelecionada === cat.id 
                        ? (isDarkMode ? `rgba(${hexToRgb(cat.cor)}, 0.1)` : `rgba(${hexToRgb(cat.cor)}, 0.05)`)
                        : 'transparent',
                      border: 'none',
                      color: themeColors.text,
                      fontSize: '14px',
                      fontWeight: '500',
                      textAlign: 'left',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (categoriaSelecionada !== cat.id) {
                        e.currentTarget.style.background = isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (categoriaSelecionada !== cat.id) {
                        e.currentTarget.style.background = 'transparent';
                      }
                    }}
                  >
                    <div style={{
                      width: '10px',
                      height: '10px',
                      borderRadius: '50%',
                      background: cat.cor || '#8B5CF6'
                    }} />
                    {cat.nome}
                  </button>
                ))}
              </div>
            </div>
          </div>
          
          {despesasPorCategoria.length > 0 ? (
            <div>
              <div style={{ maxHeight: '350px', overflowY: 'auto', marginBottom: '16px' }}>
                {despesasPorCategoria.map((despesa, index) => (
                  <div
                    key={index}
                    style={{
                      padding: '14px',
                      borderRadius: '10px',
                      background: isDarkMode ? '#1f2937' : '#f9fafb',
                      marginBottom: '10px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <p style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: themeColors.text,
                        marginBottom: '4px'
                      }}>
                        {despesa.descricao}
                      </p>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {despesa.cor && (
                          <div style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: despesa.cor
                          }} />
                        )}
                        <p style={{
                          fontSize: '12px',
                          color: themeColors.textSecondary
                        }}>
                          {despesa.categoria || 'Sem categoria'} • {new Date(despesa.data).toLocaleDateString('pt-BR')}
                        </p>
                      </div>
                    </div>
                    <p style={{
                      fontSize: '16px',
                      fontWeight: '700',
                      color: '#EF4444'
                    }}>
                      {formatCurrency(despesa.valor)}
                    </p>
                  </div>
                ))}
              </div>
              
              {/* Total da Categoria */}
              <div style={{
                padding: '16px',
                borderRadius: '10px',
                background: isDarkMode ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.05)',
                border: `2px solid ${isDarkMode ? 'rgba(239, 68, 68, 0.3)' : 'rgba(239, 68, 68, 0.2)'}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <p style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: themeColors.text
                }}>
                  Total {categoriaSelecionada !== 'todas' ? `(${despesasPorCategoria[0]?.categoria || 'Categoria'})` : ''}:
                </p>
                <p style={{
                  fontSize: '20px',
                  fontWeight: '700',
                  color: '#EF4444'
                }}>
                  {formatCurrency(despesasPorCategoria.reduce((sum, d) => sum + d.valor, 0))}
                </p>
              </div>
            </div>
          ) : (
            <div style={{
              height: '300px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: themeColors.textSecondary
            }}>
              <Filter size={48} style={{ marginBottom: '12px', opacity: 0.3 }} />
              <p style={{ fontSize: '14px' }}>
                {categoriaSelecionada === 'todas' 
                  ? 'Nenhuma despesa registrada neste mês'
                  : 'Nenhuma despesa nesta categoria'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Pendências */}
      {(pendencias.despesas_pendentes > 0 || pendencias.receitas_pendentes > 0) && (
        <div style={{
          background: isDarkMode ? '#252b3b' : 'white',
          borderRadius: '16px',
          padding: '24px',
          border: `1px solid ${themeColors.border}`,
          marginBottom: '32px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <AlertCircle size={20} color="#F59E0B" />
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: themeColors.text
            }}>
              Pendências
            </h3>
          </div>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '16px'
          }}>
            <div>
              <p style={{ fontSize: '14px', color: themeColors.textSecondary, marginBottom: '8px' }}>
                Despesas Pendentes
              </p>
              <p style={{ fontSize: '20px', fontWeight: '600', color: '#EF4444' }}>
                {formatCurrency(pendencias.despesas_pendentes)}
              </p>
            </div>
            
            <div>
              <p style={{ fontSize: '14px', color: themeColors.textSecondary, marginBottom: '8px' }}>
                Receitas Pendentes
              </p>
              <p style={{ fontSize: '20px', fontWeight: '600', color: '#10B981' }}>
                {formatCurrency(pendencias.receitas_pendentes)}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardFinanceiro;
